import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  BatonCustomQuestionKeys,
  CommonQuestionKeys,
  FieldType,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { eq, inList } from '../ast/utils.js';
import STANDARD_COMPONENTS from './standards/index.js';
import { SpecialSectionKeys } from './standards/types.js';

type ExtractedCommonQuestionKeys = Extract<
  CommonQuestionKeys,
  | CommonQuestionKeys.ADDRESS
  | CommonQuestionKeys.PHONE_NUMBER
  | CommonQuestionKeys.EMAIL
  | CommonQuestionKeys.DATE_OF_BIRTH
  | CommonQuestionKeys.GENDER
  | CommonQuestionKeys.RACE
  | CommonQuestionKeys.VETERAN
  | CommonQuestionKeys.ENGLISH
  | CommonQuestionKeys.HOUSEHOLD_SIZE
  | CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER
  | CommonQuestionKeys.AFFIRMATION
>;

export const QUESTIONS: {
  [key in ExtractedCommonQuestionKeys | BatonCustomQuestionKeys]: ApplicationQuestion;
} = {
  // PERSONAL INFORMATION
  [BatonCustomQuestionKeys.PARISH]: {
    key: 'parish',
    copy: { title: 'What parish do you live in?' },
    fields: [
      {
        type: FieldType.Text,
        copy: 'Parish',
        key: BatonCustomQuestionKeys.PARISH,
        inputType: TextInputType.Text,
      },
    ],
  },
  [CommonQuestionKeys.ADDRESS]: {
    key: CommonQuestionKeys.ADDRESS,
    copy: { title: 'What is your address?' },
    fields: [
      {
        type: FieldType.Address,
        copy: 'Address',
        key: CommonQuestionKeys.ADDRESS,
        filter: { state: 'LA' },
      },
    ],
  },
  [CommonQuestionKeys.PHONE_NUMBER]: {
    key: CommonQuestionKeys.PHONE_NUMBER,
    copy: { title: 'What is your phone number?' },
    fields: [
      {
        type: FieldType.Text,
        copy: 'Phone number',
        key: CommonQuestionKeys.PHONE_NUMBER,
        inputType: TextInputType.Phone,
      },
    ],
  },
  [CommonQuestionKeys.EMAIL]: {
    key: CommonQuestionKeys.EMAIL,
    copy: { title: 'What is your email address?' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        copy: 'Email Address',
        key: CommonQuestionKeys.EMAIL,
      },
    ],
  },
  [CommonQuestionKeys.DATE_OF_BIRTH]: {
    key: CommonQuestionKeys.DATE_OF_BIRTH,
    copy: { title: 'What is your Date of Birth?' },
    fields: [
      {
        type: FieldType.Date,
        copy: 'Date of Birth',
        key: CommonQuestionKeys.DATE_OF_BIRTH,
        validation: { required: true, allowedRange: 'past' },
        props: { helperText: 'Example: 06/01/2004' },
      },
    ],
  },
  [CommonQuestionKeys.GENDER]: {
    key: CommonQuestionKeys.GENDER,
    copy: { title: 'What is your gender identity?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Gender',
        key: 'gender.gender',
        options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'transgender-male', label: 'Transgender Male' },
          { value: 'transgender-female', label: 'Transgender Female' },
          {
            value: 'genderqueer/gender-non-conforming/gender-non-binary',
            label: 'Genderqueer/Gender non-conforming/Gender non-binary',
          },
          { value: 'questioning-or-unsure', label: 'Questioning or unsure' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'gender.otherGender',
        dynamicLogic: inList('gender.gender', 'other'),
        copy: 'Other',
      },
    ],
  },
  [CommonQuestionKeys.RACE]: {
    key: CommonQuestionKeys.RACE,
    copy: { title: 'What is your racial / ethnic background?' },
    fields: [
      {
        type: FieldType.Dropdown,
        props: { multiple: true },
        copy: 'Race',
        key: 'race.race',
        options: [
          { value: 'caucasian-white-european', label: 'Caucasian/White/European' },
          { value: 'african-american-black-african', label: 'African-American/Black/African' },
          {
            value: 'american-indian-native-american-alaskan-native',
            label: 'American Indian/Native American/Alaskan Native',
          },
          { value: 'hispanic-latinx', label: 'Hispanic/Latinx' },
          { value: 'asian-pacific-islander', label: 'Asian/Pacific Islander' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'race.otherRace',
        dynamicLogic: inList('race.race', 'other'),
        copy: 'Other',
      },
    ],
  },
  [BatonCustomQuestionKeys.MARRIED]: {
    key: BatonCustomQuestionKeys.MARRIED,
    copy: { title: 'Are you married?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.MARRIED,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.SPOUSE_NAME]: {
    key: BatonCustomQuestionKeys.SPOUSE_NAME,
    copy: { title: 'What is your spouse’s name?' },
    dynamicLogic: eq('married', 'yes'),
    fields: [
      {
        type: FieldType.Text,
        copy: 'Spouse’s Name',
        key: BatonCustomQuestionKeys.SPOUSE_NAME,
        inputType: TextInputType.Text,
      },
    ],
  },
  [BatonCustomQuestionKeys.SPOUSE_OCCUPATION]: {
    key: BatonCustomQuestionKeys.SPOUSE_OCCUPATION,
    copy: { title: 'What is your spouse’s occupation?' },
    dynamicLogic: eq('married', 'yes'),
    fields: [
      {
        type: FieldType.Text,
        copy: 'Spouse’s Occupation',
        key: BatonCustomQuestionKeys.SPOUSE_OCCUPATION,
        inputType: TextInputType.Text,
      },
    ],
  },
  [BatonCustomQuestionKeys.SPOUSE_MILITARY_STATUS]: {
    key: BatonCustomQuestionKeys.SPOUSE_MILITARY_STATUS,
    copy: { title: 'What is your spouse’s military status?' },
    dynamicLogic: eq('married', 'yes'),
    fields: [
      {
        type: FieldType.Text,
        copy: 'Spouse’s Military Status',
        key: BatonCustomQuestionKeys.SPOUSE_MILITARY_STATUS,
        inputType: TextInputType.Text,
      },
    ],
  },
  [BatonCustomQuestionKeys.ELIGIBLE_WIAWIOA]: {
    key: BatonCustomQuestionKeys.ELIGIBLE_WIAWIOA,
    copy: { title: 'Were you previously deemed eligible for the EmployBR program?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.ELIGIBLE_WIAWIOA,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.US_CITIZEN]: {
    key: BatonCustomQuestionKeys.US_CITIZEN,
    copy: { title: 'Are you a U.S. citizen?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.US_CITIZEN,
        options: YesNoOptions,
      },
    ],
  },
  [CommonQuestionKeys.VETERAN]: {
    key: CommonQuestionKeys.VETERAN,
    copy: { title: 'Are you a veteran?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.VETERAN,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.SELECTIVE_SERVICE]: {
    key: BatonCustomQuestionKeys.SELECTIVE_SERVICE,
    copy: { title: 'Are you an 18 year old male registered with selective service?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.SELECTIVE_SERVICE,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.RELATED_DISABILITY]: {
    key: BatonCustomQuestionKeys.RELATED_DISABILITY,
    copy: { title: 'Do you have a documented or service related disability?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.RELATED_DISABILITY,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.CONVICTED_OVER_25]: {
    key: BatonCustomQuestionKeys.CONVICTED_OVER_25,
    copy: {
      title: 'Are you over the age of 25 and, have been charged with and/or convicted of a felony?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.CONVICTED_OVER_25,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.DRUG_TEST]: {
    key: BatonCustomQuestionKeys.DRUG_TEST,
    copy: { title: 'Can you pass a drug test?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.DRUG_TEST,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.GRADUATE_HS]: {
    key: BatonCustomQuestionKeys.GRADUATE_HS,
    copy: { title: 'Did you graduate from high school?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.GRADUATE_HS,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.FOSTER_CARE]: {
    key: BatonCustomQuestionKeys.FOSTER_CARE,
    copy: { title: 'Are you in foster care or aged out of foster care?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.FOSTER_CARE,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.PARENTING]: {
    key: BatonCustomQuestionKeys.PARENTING,
    copy: { title: 'Are you pregnant or parenting?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.PARENTING,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.FELONY]: {
    key: BatonCustomQuestionKeys.FELONY,
    copy: { title: 'Have you ever been charged with and/or convicted of a felony?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.FELONY,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.INCARCERATED]: {
    key: BatonCustomQuestionKeys.INCARCERATED,
    copy: { title: 'Are your parents/guardian currently incarcerated?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.INCARCERATED,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.PARENTS_UNEMPLOYED]: {
    key: BatonCustomQuestionKeys.PARENTS_UNEMPLOYED,
    copy: { title: 'Are your parents/guardian currently unemployed?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.PARENTS_UNEMPLOYED,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.REPEAT_GRADE_LEVEL]: {
    key: BatonCustomQuestionKeys.REPEAT_GRADE_LEVEL,
    copy: { title: 'Have you ever been required to repeat a grade level?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.REPEAT_GRADE_LEVEL,
        options: YesNoOptions,
      },
    ],
  },
  [CommonQuestionKeys.ENGLISH]: {
    key: CommonQuestionKeys.ENGLISH,
    copy: { title: 'Is English your first language?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.ENGLISH,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.SEX_TRAFFICKING]: {
    key: BatonCustomQuestionKeys.SEX_TRAFFICKING,
    copy: { title: 'Are you a victim of sex trafficking?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.SEX_TRAFFICKING,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.HOMELESS]: {
    key: BatonCustomQuestionKeys.HOMELESS,
    copy: { title: 'Are you currently homeless and/or a runaway?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.HOMELESS,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.NATURAL_DISASTER]: {
    key: BatonCustomQuestionKeys.NATURAL_DISASTER,
    copy: {
      title:
        'Have you been a victim of a federally declared natural disaster in the past five years?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.NATURAL_DISASTER,
        options: YesNoOptions,
      },
    ],
  },
  // EDUCATION
  [BatonCustomQuestionKeys.APPLIED_FAFSA]: {
    key: BatonCustomQuestionKeys.APPLIED_FAFSA,
    copy: { title: 'Have you applied for FAFSA/ Pell Grant and other financial assistance?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.APPLIED_FAFSA,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.ATTENDING_SCHOOL]: {
    key: BatonCustomQuestionKeys.ATTENDING_SCHOOL,
    copy: { title: 'Are you currently attending school, training, or educational classes?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.ATTENDING_SCHOOL,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.LIST_TRAINING_HS]: {
    key: BatonCustomQuestionKeys.LIST_TRAINING_HS,
    copy: { title: 'Please list any education/training' },
    dynamicLogic: eq('attendingSchool', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        useLabelPrefix: false,
        key: BatonCustomQuestionKeys.LIST_TRAINING_HS,
        copy: 'Education',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'School or Course',
            key: 'school',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Grade Level or Hours',
            key: 'grade',
          },
        ],
      },
    ],
  },
  [BatonCustomQuestionKeys.COMPLETION_SCHOOL]: {
    key: BatonCustomQuestionKeys.COMPLETION_SCHOOL,
    copy: { title: 'Did you complete high school or get a GED?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.COMPLETION_SCHOOL,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.EDUCATION_AFTER_HS]: {
    key: BatonCustomQuestionKeys.EDUCATION_AFTER_HS,
    copy: { title: 'Have you received any education/training after high school?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.EDUCATION_AFTER_HS,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.LIST_TRAINING_AFTER_HS]: {
    key: BatonCustomQuestionKeys.LIST_TRAINING_AFTER_HS,
    copy: { title: 'Please list any education/training after high school' },
    dynamicLogic: eq('educationAfterHS', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        key: BatonCustomQuestionKeys.LIST_TRAINING_AFTER_HS,
        copy: 'Education',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'School or Course',
            key: 'school',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Grade Level or Hours',
            key: 'grade',
          },
        ],
      },
    ],
  },
  // HOUSEHOLD INCOME
  [CommonQuestionKeys.HOUSEHOLD_SIZE]: {
    key: CommonQuestionKeys.HOUSEHOLD_SIZE,
    copy: { title: 'How many people are in your household?', intro: '(including yourself)' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Number of people in household',
        key: CommonQuestionKeys.HOUSEHOLD_SIZE,
      },
    ],
  },

  [BatonCustomQuestionKeys.HOUSEHOLD_SALARY]: {
    key: BatonCustomQuestionKeys.HOUSEHOLD_SALARY,
    copy: { title: 'What is your household’s salary range?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.HOUSEHOLD_SALARY,
        options: [
          { value: '$0-$14,580', label: '$0 - $14,580' },
          { value: '$14,580-$19,720', label: '$14,580 - $19,720' },
          { value: '$19,721-$24,860', label: '$19,721 - $24,860' },
          { value: '$24,861-$30,000', label: '$24,861 - $30,000' },
          { value: '$30,001-$35,140', label: '$30,001 - $35,140' },
          { value: '$35,141-$40,280', label: '$35,141 - $40,280' },
          { value: '$40,281–or more', label: '$40,281 – or more' },
        ],
      },
    ],
  },

  [BatonCustomQuestionKeys.RECIPIENT_OTHER]: {
    key: BatonCustomQuestionKeys.RECIPIENT_OTHER,
    copy: {
      title: 'Are you a recipient of any other public assistance funding?',
      intro: 'SNAP, TANF (formerly welfare), or WIC',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.RECIPIENT_OTHER,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.RECIPIENT_TANF]: {
    key: BatonCustomQuestionKeys.RECIPIENT_TANF,
    copy: { title: 'Are you a recipient of TANF?' },
    dynamicLogic: eq('recipientOther', 'yes'),
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.RECIPIENT_TANF,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.RECIPIENT_SNAP]: {
    key: BatonCustomQuestionKeys.RECIPIENT_SNAP,
    copy: { title: 'Are you a recipient of SNAP/Food Stamps?' },
    dynamicLogic: eq('recipientOther', 'yes'),
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.RECIPIENT_SNAP,
        options: YesNoOptions,
      },
    ],
  },

  [BatonCustomQuestionKeys.LIST_MEMBERS_HOUSE]: {
    key: BatonCustomQuestionKeys.LIST_MEMBERS_HOUSE,
    copy: { title: 'Please list all of the members of your house or home' },
    dynamicLogic: eq('recipientOther', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        key: BatonCustomQuestionKeys.LIST_MEMBERS_HOUSE,
        copy: 'Household Members',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Member',
            key: 'member',
          },
        ],
      },
    ],
  },
  [BatonCustomQuestionKeys.CURRENT_EMPLOYED]: {
    key: BatonCustomQuestionKeys.CURRENT_EMPLOYED,
    copy: { title: 'Are you currently employed?' },
    dynamicLogic: eq('recipientOther', 'yes'),
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.CURRENT_EMPLOYED,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.LAID_OFF_EMPLOYMENT]: {
    key: BatonCustomQuestionKeys.LAID_OFF_EMPLOYMENT,
    copy: { title: 'Were you laid-off from most recent employment?' },
    dynamicLogic: eq('recipientOther', 'yes'),
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.LAID_OFF_EMPLOYMENT,
        options: YesNoOptions,
      },
    ],
  },
  [BatonCustomQuestionKeys.ELIGIBLE_BENEFITS]: {
    key: BatonCustomQuestionKeys.ELIGIBLE_BENEFITS,
    copy: { title: 'Are you eligible to receive Unemployment Benefits?' },
    dynamicLogic: eq('recipientOther', 'yes'),
    fields: [
      {
        type: FieldType.RadioList,
        key: BatonCustomQuestionKeys.ELIGIBLE_BENEFITS,
        options: YesNoOptions,
      },
    ],
  },

  [BatonCustomQuestionKeys.LIST_PREVIOUS_EMPLOYMENTS]: {
    key: BatonCustomQuestionKeys.LIST_PREVIOUS_EMPLOYMENTS,
    copy: { title: 'Please list your previous employment' },
    dynamicLogic: eq('recipientOther', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        key: BatonCustomQuestionKeys.LIST_PREVIOUS_EMPLOYMENTS,
        copy: 'Employer',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name',
            key: 'name',
          },
        ],
      },
    ],
  },

  // DOCUMENTS
  [BatonCustomQuestionKeys.SOCIAL_SECURITY_NUMBER_DOC]: {
    key: BatonCustomQuestionKeys.SOCIAL_SECURITY_NUMBER_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: 'Please upload documents to help us verify your Social Security Number.',
    },
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.socialSecurityNumberDoc',
        copy: {
          title: 'Supporting Documents',
          description:
            'Examples include your Social Security Card, W-2, Passport, Military ID, Administration Medical Card, Federal/State ID, DD 214/Report of Transfer/Veterans, Letter from Social Security Administration.',
        },
      },
    ],
  },
  [BatonCustomQuestionKeys.DOB_DOC]: {
    key: BatonCustomQuestionKeys.DOB_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: 'Please upload documents to help us verify your Date of Birth.',
    },
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.dobDOC',
        copy: {
          title: 'Supporting Documents',
          description:
            'Examples include your birth certificate, driver’s license, federal/state ID, hospital record of birth, passport, Public Assistance/Social Service Records, School Records/Identification Card/Work Permit.',
        },
      },
    ],
  },
  [BatonCustomQuestionKeys.CITIZENSHIP_DOC]: {
    key: BatonCustomQuestionKeys.CITIZENSHIP_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: 'Please upload documents to help us verify your citizenship/alien status.',
    },
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.citizenshipDOC',
        copy: {
          title: 'Supplemental Documents',
          description: `Examples include your birth certificate, Alien Registration Card Indicating Right
to Work, Foreign Passport Stamped Eligible to Work, hospital record of birth, US
passport, Naturalization Certification, Voter Registration Card.`,
        },
      },
    ],
  },
  [BatonCustomQuestionKeys.FAMILY_ASSISTANCE_DOC]: {
    key: BatonCustomQuestionKeys.FAMILY_ASSISTANCE_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: 'Please upload documents to help us verify your family income/public assistance.',
    },
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.familyAssistanceDoc',
        copy: {
          title: 'Supporting Documents',
          description:
            "Examples include your Check Stubs (Recent Six Months),Employer's Statement (Company Letterhead), Food Stamp Record/Social Service Records, Child Support,SSI/SSDI, Applicant Statement.",
        },
      },
    ],
  },
  [BatonCustomQuestionKeys.RESIDENCE_DOC]: {
    key: BatonCustomQuestionKeys.RESIDENCE_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: 'Please upload documents to help us verify your residence/address.',
    },
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.residenceDoc',
        copy: {
          title: 'Supporting Documents',
          description:
            "Examples include your Public Assistance Record/Social Service, School Records, Utility Bill (current), Dated Mail (recent postmark), Driver's License (current validation), Landlord Statement/Lease, Voter Registration.",
        },
      },
    ],
  },
  [BatonCustomQuestionKeys.SELECTIVE_SERVICE_DOC]: {
    key: BatonCustomQuestionKeys.SELECTIVE_SERVICE_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: `
Please upload documents to help us verify your selective service/draft status.
[https://www.sss.gov/register/](https://www.sss.gov/register/)`,
    },
    skippable: true,
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.selectiveServiceDoc',
        copy: {
          title: 'Supporting Documents',
          description:
            'Examples include your Selective Service Registration Card, Selective Service Verification Form, Stamped Post Office Registration Receipt, Internet On-line access/Verification, DD214, Selective Service Acknowledgement Letter/Request.',
        },
      },
    ],
  },
  [BatonCustomQuestionKeys.DISABILITY_DOC]: {
    key: BatonCustomQuestionKeys.DISABILITY_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: 'Please upload documents to help us verify your disability information.',
    },
    skippable: true,
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.disabilityDoc',
        copy: {
          title: 'Supporting Documents',
          description:
            "Examples include your Documentation from Vocational Service Agency, Doctor's Medical Statement/Evaluation Summary, Formalized Assessment (Medical/Educational).",
        },
      },
    ],
  },
  [BatonCustomQuestionKeys.TRAINING_DOC]: {
    key: BatonCustomQuestionKeys.TRAINING_DOC,
    copy: {
      title: 'Supplemental Documents',
      intro: 'Please upload documents to help us verify your education/prior trainings.',
    },
    dynamicLogic: eq('recipientOther', 'yes'),
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.trainingDoc',
        copy: {
          title: 'Supporting Documents',
          description:
            'Examples include your School Verification Form, High School Diploma/Credential, GED/HISET Credential/Documented Statement, College Diploma/Credential, Other Certifications/Credentials.',
        },
      },
    ],
  },
  // PARENT/GUARDIAN
  [BatonCustomQuestionKeys.PARENT_GUARDIAN]: {
    key: BatonCustomQuestionKeys.PARENT_GUARDIAN,
    copy: { title: 'Please list the information for your parent or guardian.' },
    fields: [
      {
        type: FieldType.Complex,
        key: 'listParentsGuardians',
        copy: 'Parent/Guardian',
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            copy: 'Name',
            key: 'name',
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Email,
            copy: 'Email',
            key: CommonQuestionKeys.EMAIL,
          },
          {
            type: FieldType.Text,
            inputType: TextInputType.Phone,
            copy: 'Phone Number',
            key: CommonQuestionKeys.PHONE_NUMBER,
          },
          {
            type: FieldType.Date,
            copy: 'Date of Birth',
            key: CommonQuestionKeys.DATE_OF_BIRTH,
            validation: { required: true, allowedRange: 'past' },
            props: { helperText: 'Example: 06/01/2004' },
          },
        ],
      },
    ],
  },
  //REVIEW
  [CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER]: {
    key: CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER,
    copy: {
      title: 'Did someone help you fill out this application?',
      intro:
        'Please provide the following information about the person that helped you complete this application.',
    },
    skippable: true,
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Helper Name',
        key: 'submission.assistance.helper.name',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Organization (if applicable)',
        key: 'submission.assistance.helper.organization',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        copy: 'Helper Email',
        key: 'submission.assistance.helper.email',
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Phone,
        copy: 'Helper Phone Number',
        key: 'submission.assistance.helper.phone',
        validation: { required: false },
      },
    ],
  },
  [CommonQuestionKeys.AFFIRMATION]: {
    key: CommonQuestionKeys.AFFIRMATION,
    copy: {
      title: 'Before submitting the application, please confirm this statement:',
      intro: 'Please review and confirm the statement by selecting the checkbox below.',
    },
    fields: [
      {
        type: FieldType.Checkbox,
        key: CommonQuestionKeys.AFFIRMATION,
        copy: "I, {APPLICANT_NAME}, certify under penalty of law, based on information and belief formed after reasonable inquiry, the statements and information contained in these documents are true, accurate and complete. I acknowledge that my information can be used by {PARTNER_NAME}'s staff to refer me to other programs.",
      },
    ],
  },
};

const COMMON_SECTIONS: ApplicationSection[] = [
  {
    key: 'personalInfo',
    name: 'Personal Information',
    overview: {
      description: `
In this section we’ll gather some personal information to see if you’re a good fit for the
Mayor’s Youth Workforce summer program.`,
      title: 'Let’s start with some personal information about you',
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'Personal Information',
        questions: [
          QUESTIONS.parish,
          QUESTIONS[CommonQuestionKeys.ADDRESS],
          QUESTIONS.phoneNumber,
          QUESTIONS.email,
          QUESTIONS.dateOfBirth,
          QUESTIONS.gender,
          QUESTIONS.race,
          QUESTIONS.married,
          QUESTIONS.spouseName,
          QUESTIONS.spouseOccupation,
          QUESTIONS.spouseMilitaryStatus,
          QUESTIONS.eligibleWIAWIOA,
          QUESTIONS.usCitizen,
          QUESTIONS.veteran,
          QUESTIONS.selectiveService,
          QUESTIONS.relatedDisability,
          QUESTIONS.convictedOver25,
          QUESTIONS.drugTest,
          QUESTIONS.graduateHS,
          QUESTIONS.fosterCare,
          QUESTIONS.parenting,
          QUESTIONS.felony,
          QUESTIONS.incarcerated,
          QUESTIONS.parentsUnemployed,
          QUESTIONS.repeatGradeLevel,
          QUESTIONS.english,
          QUESTIONS.sexTrafficking,
          QUESTIONS.homeless,
          QUESTIONS.naturalDisaster,
        ],
      },
    ],
  },
  // Parent/Guardian
  {
    key: 'parentGuardianInfo',
    name: 'Parent/Guardian Info',
    overview: {
      description: `
In this section, we’ll gather some information about your parent or guardian in case we
need to contact them.`,
      title: 'Parent/Guardian Info',
    },
    questionGroups: [
      {
        key: 'educationInfo',
        name: 'Parent/Guardian Info',
        questions: [QUESTIONS.parentGuardian],
      },
    ],
  },
  // Education Section
  {
    key: 'educationInfo',
    name: 'Education',
    overview: {
      title: 'Education',
      description: 'In this section we’ll gather some information on your level of education.',
    },
    questionGroups: [
      {
        key: 'educationInfo',
        name: 'Education',
        questions: [
          QUESTIONS.appliedFAFSA,
          QUESTIONS.attendingSchool,
          QUESTIONS.listTrainingHS,
          QUESTIONS.completionSchool,
          QUESTIONS.educationAfterHS,
          QUESTIONS.listTrainingAfterHS,
        ],
      },
    ],
  },
  // Household Income Section
  {
    key: 'householdIncomeSection',
    name: 'Household Income',
    overview: {
      title: 'Household Income',
      description:
        'In this section we’ll gather some information related to your household’s income.',
    },
    questionGroups: [
      {
        key: 'householdIncomeQuestionGroup',
        name: 'Household Income',
        questions: [
          QUESTIONS[CommonQuestionKeys.HOUSEHOLD_SIZE],
          QUESTIONS.householdSalary,
          QUESTIONS.recipientOther,
          QUESTIONS.recipientTANF,
          QUESTIONS.recipientSNAP,
          QUESTIONS.listMembersHouse,
          QUESTIONS.currentlyEmployed,
          QUESTIONS.laidOffEmployment,
          QUESTIONS.eligibleBenefits,
          QUESTIONS.listPreviousEmployments,
          QUESTIONS.socialSecurityNumberDoc,
          QUESTIONS.dobDOC,
          QUESTIONS.citizenshipDOC,
          QUESTIONS.familyAssistanceDoc,
          QUESTIONS.residenceDoc,
          QUESTIONS[BatonCustomQuestionKeys.SELECTIVE_SERVICE_DOC],
          QUESTIONS[BatonCustomQuestionKeys.DISABILITY_DOC],
          QUESTIONS.trainingDoc,
        ],
      },
    ],
  },
  STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'What is MYWE?',
      body: 'The Mayor’s Youth Workforce Experience creates a pathway to success by providing young adults with a chance to discover interests, build skills, and explore careers through experiences that can serve as the foundation for educational and professional success.',
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: 'Apply for Summer Job Placement',
        message:
          'You could be found eligible to be placed in a new job. Start your application to be placed in a new job for this upcoming summer.',
      },
    ],
  },
  sections: COMMON_SECTIONS,
};
export default CONFIG;
